import {useWorkspace} from '../hooks/useWorkspace';
import {dataClient} from '../data/dataClient';
import ChatWindow from '../components/ChatWindow';
import {TeamRoster} from '../components/BotComponents';
import FinancePlansSidebar from '../components/FinancePlansSidebar';
import AdsBriefsSidebar from '../components/AdsBriefsSidebar';

const content = {
  finance: {
    eyebrow: 'Finance',
    title: 'Ask first. Model second.',
    description: 'The Finance Lead consults revenue and cash-flow specialists, then returns one decision-ready answer.',
    plan: 'Save as Finance Plan'
  },
  ads: {
    eyebrow: 'Ads',
    title: 'Strategy before spend.',
    description: 'Build an ad campaign brief through conversation, specialist consultation, and a clear approval point.',
    plan: 'Save as Campaign Brief'
  }
};

export default function ChatModule({module}) {
  const state = useWorkspace();
  const team = state.teams.find((item) => item.module === module);
  const bots = team ? state.bots.filter((bot) => bot.teamId === team.id) : [];
  const lead = bots.find((bot) => bot.isLead);
  const copy = content[module] || {
    eyebrow: module,
    title: `${module.charAt(0).toUpperCase() + module.slice(1)} Workspace`,
    description: 'Consult with your AI workforce and save actionable plans.',
    plan: 'Save as Plan'
  };

  const currentMessages = state.messages?.[module] || [];
  const lastBotMessage = [...currentMessages].reverse().find((m) => m.sender === 'bot');

  async function savePlan() {
    if (!lead) return;

    const title = module === 'finance'
      ? `Financial Forecast (${new Date().toLocaleDateString('en-US', {month: 'short', day: 'numeric'})})`
      : `Campaign Brief (${new Date().toLocaleDateString('en-US', {month: 'short', day: 'numeric'})})`;

    // Link the plan to the producing chat session and message
    const plan = await dataClient.create('plans', {
      title,
      status: 'draft',
      version: 1,
      module,
      botId: lead.id,
      sessionId: `session_${module}`,
      sourceMessageId: lastBotMessage?.id || null,
      content: {
        summary: lastBotMessage?.text || 'Produced through consultation in workspace chat.',
        createdFromSession: `session_${module}`
      }
    });

    // If in Ads, also materialize an adCampaignBrief with copy variants
    if (module === 'ads') {
      const summaryText = lastBotMessage?.text || 'Targeted campaign brief';
      await dataClient.create('adCampaignBriefs', {
        title: plan.title,
        plan: plan.id,
        status: 'draft',
        targetAudience: state.business?.audience || 'Target customer segment',
        keyMessage: summaryText.length > 140 ? `${summaryText.slice(0, 140)}…` : summaryText,
        variants: [
          'Variant A: Leading with immediate value and distinctive craftsmanship.',
          'Variant B: Focused on customer outcomes, durability, and mindful design.',
          'Variant C: Direct seasonal invitation with authentic proof points.'
        ]
      });
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <span className="eyebrow">{copy.eyebrow}</span>
        <h1>{copy.title}</h1>
        <p>{copy.description}</p>
      </header>

      <div className="chat-layout">
        <ChatWindow
          module={module}
          lead={lead}
          messages={currentMessages}
          planLabel={copy.plan}
          onPlan={savePlan}
        />

        {module === 'finance' ? (
          <FinancePlansSidebar state={state} team={team} bots={bots} />
        ) : module === 'ads' ? (
          <AdsBriefsSidebar state={state} team={team} bots={bots} />
        ) : (
          <TeamRoster team={team} bots={bots} />
        )}
      </div>
    </div>
  );
}
